import * as z from 'zod';

export const CreateItemRequest = z.object({
  item: z
    .object({
      _id: z
        .string()
        .describe('Item ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .optional()
        .nullable(),
      draftItemId: z
        .string()
        .describe('Draft Item ID that this item originated from')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      galleryId: z
        .string()
        .describe('Gallery ID that the item is part of')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      siteVersion: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe('Represents the site version when the item was published')
        .optional()
        .nullable(),
      title: z.string().max(50).optional().nullable(),
      link: z
        .object({
          text: z
            .string()
            .describe(
              "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
            )
            .max(1000)
            .optional()
            .nullable(),
          url: z
            .string()
            .describe('Target URL of the link.')
            .url()
            .optional()
            .nullable(),
        })
        .describe('Link to an internal or external page')
        .optional(),
      media: z
        .intersection(
          z.object({ type: z.enum(['UNDEFINED', 'IMAGE']).optional() }),
          z.xor([
            z.object({ imageOptions: z.never().optional() }),
            z.object({
              imageOptions: z
                .object({
                  imageInfo: z
                    .string()
                    .describe('Information about the Wix Media image.')
                    .optional(),
                  focalPoint: z
                    .object({
                      x: z
                        .number()
                        .describe('X-coordinate of the focal point.')
                        .optional(),
                      y: z
                        .number()
                        .describe('Y-coordinate of the focal point.')
                        .optional(),
                    })
                    .describe('Focal point of the image.')
                    .optional(),
                  exif: z
                    .record(z.string(), z.any())
                    .describe(
                      'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                    )
                    .optional()
                    .nullable(),
                  quality: z
                    .number()
                    .int()
                    .describe(
                      'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                    )
                    .min(30)
                    .max(100)
                    .optional()
                    .nullable(),
                  unsharpMasking: z
                    .object({
                      amount: z
                        .number()
                        .describe(
                          'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                        )
                        .min(0)
                        .max(5)
                        .optional()
                        .nullable(),
                      radius: z
                        .number()
                        .describe(
                          'Unsharp masking radius in pixels. Controls the sharpening width.'
                        )
                        .optional()
                        .nullable(),
                      threshold: z
                        .number()
                        .describe(
                          'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                        )
                        .min(0)
                        .max(1)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                    )
                    .optional(),
                  altText: z
                    .string()
                    .describe('Image alt text, translatable.')
                    .max(5000)
                    .optional()
                    .nullable(),
                })
                .describe('image item'),
            }),
          ])
        )
        .describe('media of the item')
        .optional(),
      _createdDate: z.date().optional().nullable(),
      _updatedDate: z.date().optional().nullable(),
      description: z.string().max(1500).optional().nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
    })
    .describe('Item to be created.'),
});
export const CreateItemResponse = z.object({
  _id: z
    .string()
    .describe('Item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .optional()
    .nullable(),
  draftItemId: z
    .string()
    .describe('Draft Item ID that this item originated from')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  galleryId: z
    .string()
    .describe('Gallery ID that the item is part of')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  siteVersion: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe('Represents the site version when the item was published')
    .optional()
    .nullable(),
  title: z.string().max(50).optional().nullable(),
  link: z
    .object({
      text: z
        .string()
        .describe(
          "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
        )
        .max(1000)
        .optional()
        .nullable(),
      url: z
        .string()
        .describe('Target URL of the link.')
        .url()
        .optional()
        .nullable(),
    })
    .describe('Link to an internal or external page')
    .optional(),
  media: z
    .intersection(
      z.object({
        type: z
          .enum(['UNDEFINED', 'IMAGE'])
          .describe("item's media type")
          .optional(),
      }),
      z.xor([
        z.object({ imageOptions: z.never().optional() }),
        z.object({
          imageOptions: z
            .object({
              imageInfo: z
                .string()
                .describe('Information about the Wix Media image.')
                .optional(),
              focalPoint: z
                .object({
                  x: z
                    .number()
                    .describe('X-coordinate of the focal point.')
                    .optional(),
                  y: z
                    .number()
                    .describe('Y-coordinate of the focal point.')
                    .optional(),
                })
                .describe('Focal point of the image.')
                .optional(),
              exif: z
                .record(z.string(), z.any())
                .describe(
                  'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                )
                .optional()
                .nullable(),
              quality: z
                .number()
                .int()
                .describe(
                  'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                )
                .min(30)
                .max(100)
                .optional()
                .nullable(),
              unsharpMasking: z
                .object({
                  amount: z
                    .number()
                    .describe(
                      'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                    )
                    .min(0)
                    .max(5)
                    .optional()
                    .nullable(),
                  radius: z
                    .number()
                    .describe(
                      'Unsharp masking radius in pixels. Controls the sharpening width.'
                    )
                    .optional()
                    .nullable(),
                  threshold: z
                    .number()
                    .describe(
                      'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                    )
                    .min(0)
                    .max(1)
                    .optional()
                    .nullable(),
                })
                .describe(
                  '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                )
                .optional(),
              altText: z
                .string()
                .describe('Image alt text, translatable.')
                .max(5000)
                .optional()
                .nullable(),
            })
            .describe('image item'),
        }),
      ])
    )
    .describe('media of the item')
    .optional(),
  _createdDate: z.date().optional().nullable(),
  _updatedDate: z.date().optional().nullable(),
  description: z.string().max(1500).optional().nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data Extensions')
    .optional(),
});
export const GetItemRequest = z.object({
  itemId: z
    .string()
    .describe('ID of the Item to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetItemResponse = z.object({
  _id: z
    .string()
    .describe('Item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .optional()
    .nullable(),
  draftItemId: z
    .string()
    .describe('Draft Item ID that this item originated from')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  galleryId: z
    .string()
    .describe('Gallery ID that the item is part of')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  siteVersion: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe('Represents the site version when the item was published')
    .optional()
    .nullable(),
  title: z.string().max(50).optional().nullable(),
  link: z
    .object({
      text: z
        .string()
        .describe(
          "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
        )
        .max(1000)
        .optional()
        .nullable(),
      url: z
        .string()
        .describe('Target URL of the link.')
        .url()
        .optional()
        .nullable(),
    })
    .describe('Link to an internal or external page')
    .optional(),
  media: z
    .intersection(
      z.object({
        type: z
          .enum(['UNDEFINED', 'IMAGE'])
          .describe("item's media type")
          .optional(),
      }),
      z.xor([
        z.object({ imageOptions: z.never().optional() }),
        z.object({
          imageOptions: z
            .object({
              imageInfo: z
                .string()
                .describe('Information about the Wix Media image.')
                .optional(),
              focalPoint: z
                .object({
                  x: z
                    .number()
                    .describe('X-coordinate of the focal point.')
                    .optional(),
                  y: z
                    .number()
                    .describe('Y-coordinate of the focal point.')
                    .optional(),
                })
                .describe('Focal point of the image.')
                .optional(),
              exif: z
                .record(z.string(), z.any())
                .describe(
                  'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                )
                .optional()
                .nullable(),
              quality: z
                .number()
                .int()
                .describe(
                  'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                )
                .min(30)
                .max(100)
                .optional()
                .nullable(),
              unsharpMasking: z
                .object({
                  amount: z
                    .number()
                    .describe(
                      'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                    )
                    .min(0)
                    .max(5)
                    .optional()
                    .nullable(),
                  radius: z
                    .number()
                    .describe(
                      'Unsharp masking radius in pixels. Controls the sharpening width.'
                    )
                    .optional()
                    .nullable(),
                  threshold: z
                    .number()
                    .describe(
                      'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                    )
                    .min(0)
                    .max(1)
                    .optional()
                    .nullable(),
                })
                .describe(
                  '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                )
                .optional(),
              altText: z
                .string()
                .describe('Image alt text, translatable.')
                .max(5000)
                .optional()
                .nullable(),
            })
            .describe('image item'),
        }),
      ])
    )
    .describe('media of the item')
    .optional(),
  _createdDate: z.date().optional().nullable(),
  _updatedDate: z.date().optional().nullable(),
  description: z.string().max(1500).optional().nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data Extensions')
    .optional(),
});
export const UpdateItemRequest = z.object({
  _id: z
    .string()
    .describe('Item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  item: z
    .object({
      _id: z
        .string()
        .describe('Item ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z.string().regex(/^-?\d+$/, 'Must be a valid Int64 string'),
      draftItemId: z
        .string()
        .describe('Draft Item ID that this item originated from')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      galleryId: z
        .string()
        .describe('Gallery ID that the item is part of')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      siteVersion: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe('Represents the site version when the item was published')
        .optional()
        .nullable(),
      title: z.string().max(50).optional().nullable(),
      link: z
        .object({
          text: z
            .string()
            .describe(
              "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
            )
            .max(1000)
            .optional()
            .nullable(),
          url: z
            .string()
            .describe('Target URL of the link.')
            .url()
            .optional()
            .nullable(),
        })
        .describe('Link to an internal or external page')
        .optional(),
      media: z
        .intersection(
          z.object({ type: z.enum(['UNDEFINED', 'IMAGE']).optional() }),
          z.xor([
            z.object({ imageOptions: z.never().optional() }),
            z.object({
              imageOptions: z
                .object({
                  imageInfo: z
                    .string()
                    .describe('Information about the Wix Media image.')
                    .optional(),
                  focalPoint: z
                    .object({
                      x: z
                        .number()
                        .describe('X-coordinate of the focal point.')
                        .optional(),
                      y: z
                        .number()
                        .describe('Y-coordinate of the focal point.')
                        .optional(),
                    })
                    .describe('Focal point of the image.')
                    .optional(),
                  exif: z
                    .record(z.string(), z.any())
                    .describe(
                      'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                    )
                    .optional()
                    .nullable(),
                  quality: z
                    .number()
                    .int()
                    .describe(
                      'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                    )
                    .min(30)
                    .max(100)
                    .optional()
                    .nullable(),
                  unsharpMasking: z
                    .object({
                      amount: z
                        .number()
                        .describe(
                          'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                        )
                        .min(0)
                        .max(5)
                        .optional()
                        .nullable(),
                      radius: z
                        .number()
                        .describe(
                          'Unsharp masking radius in pixels. Controls the sharpening width.'
                        )
                        .optional()
                        .nullable(),
                      threshold: z
                        .number()
                        .describe(
                          'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                        )
                        .min(0)
                        .max(1)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                    )
                    .optional(),
                  altText: z
                    .string()
                    .describe('Image alt text, translatable.')
                    .max(5000)
                    .optional()
                    .nullable(),
                })
                .describe('image item'),
            }),
          ])
        )
        .describe('media of the item')
        .optional(),
      _createdDate: z.date().optional().nullable(),
      _updatedDate: z.date().optional().nullable(),
      description: z.string().max(1500).optional().nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
    })
    .describe('Item to be updated, may be partial.'),
});
export const UpdateItemResponse = z.object({
  _id: z
    .string()
    .describe('Item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .optional()
    .nullable(),
  draftItemId: z
    .string()
    .describe('Draft Item ID that this item originated from')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  galleryId: z
    .string()
    .describe('Gallery ID that the item is part of')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  siteVersion: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe('Represents the site version when the item was published')
    .optional()
    .nullable(),
  title: z.string().max(50).optional().nullable(),
  link: z
    .object({
      text: z
        .string()
        .describe(
          "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
        )
        .max(1000)
        .optional()
        .nullable(),
      url: z
        .string()
        .describe('Target URL of the link.')
        .url()
        .optional()
        .nullable(),
    })
    .describe('Link to an internal or external page')
    .optional(),
  media: z
    .intersection(
      z.object({
        type: z
          .enum(['UNDEFINED', 'IMAGE'])
          .describe("item's media type")
          .optional(),
      }),
      z.xor([
        z.object({ imageOptions: z.never().optional() }),
        z.object({
          imageOptions: z
            .object({
              imageInfo: z
                .string()
                .describe('Information about the Wix Media image.')
                .optional(),
              focalPoint: z
                .object({
                  x: z
                    .number()
                    .describe('X-coordinate of the focal point.')
                    .optional(),
                  y: z
                    .number()
                    .describe('Y-coordinate of the focal point.')
                    .optional(),
                })
                .describe('Focal point of the image.')
                .optional(),
              exif: z
                .record(z.string(), z.any())
                .describe(
                  'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                )
                .optional()
                .nullable(),
              quality: z
                .number()
                .int()
                .describe(
                  'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                )
                .min(30)
                .max(100)
                .optional()
                .nullable(),
              unsharpMasking: z
                .object({
                  amount: z
                    .number()
                    .describe(
                      'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                    )
                    .min(0)
                    .max(5)
                    .optional()
                    .nullable(),
                  radius: z
                    .number()
                    .describe(
                      'Unsharp masking radius in pixels. Controls the sharpening width.'
                    )
                    .optional()
                    .nullable(),
                  threshold: z
                    .number()
                    .describe(
                      'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                    )
                    .min(0)
                    .max(1)
                    .optional()
                    .nullable(),
                })
                .describe(
                  '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                )
                .optional(),
              altText: z
                .string()
                .describe('Image alt text, translatable.')
                .max(5000)
                .optional()
                .nullable(),
            })
            .describe('image item'),
        }),
      ])
    )
    .describe('media of the item')
    .optional(),
  _createdDate: z.date().optional().nullable(),
  _updatedDate: z.date().optional().nullable(),
  description: z.string().max(1500).optional().nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data Extensions')
    .optional(),
});
export const DeleteItemRequest = z.object({
  itemId: z
    .string()
    .describe('Id of the Item to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteItemResponse = z.object({});
export const QueryItemsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('WQL expression.'),
});
export const QueryItemsResponse = z.object({
  items: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .optional()
          .nullable(),
        draftItemId: z
          .string()
          .describe('Draft Item ID that this item originated from')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        galleryId: z
          .string()
          .describe('Gallery ID that the item is part of')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        siteVersion: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe('Represents the site version when the item was published')
          .optional()
          .nullable(),
        title: z.string().max(50).optional().nullable(),
        link: z
          .object({
            text: z
              .string()
              .describe(
                "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
              )
              .max(1000)
              .optional()
              .nullable(),
            url: z
              .string()
              .describe('Target URL of the link.')
              .url()
              .optional()
              .nullable(),
          })
          .describe('Link to an internal or external page')
          .optional(),
        media: z
          .intersection(
            z.object({
              type: z
                .enum(['UNDEFINED', 'IMAGE'])
                .describe("item's media type")
                .optional(),
            }),
            z.xor([
              z.object({ imageOptions: z.never().optional() }),
              z.object({
                imageOptions: z
                  .object({
                    imageInfo: z
                      .string()
                      .describe('Information about the Wix Media image.')
                      .optional(),
                    focalPoint: z
                      .object({
                        x: z
                          .number()
                          .describe('X-coordinate of the focal point.')
                          .optional(),
                        y: z
                          .number()
                          .describe('Y-coordinate of the focal point.')
                          .optional(),
                      })
                      .describe('Focal point of the image.')
                      .optional(),
                    exif: z
                      .record(z.string(), z.any())
                      .describe(
                        'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                      )
                      .optional()
                      .nullable(),
                    quality: z
                      .number()
                      .int()
                      .describe(
                        'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                      )
                      .min(30)
                      .max(100)
                      .optional()
                      .nullable(),
                    unsharpMasking: z
                      .object({
                        amount: z
                          .number()
                          .describe(
                            'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                          )
                          .min(0)
                          .max(5)
                          .optional()
                          .nullable(),
                        radius: z
                          .number()
                          .describe(
                            'Unsharp masking radius in pixels. Controls the sharpening width.'
                          )
                          .optional()
                          .nullable(),
                        threshold: z
                          .number()
                          .describe(
                            'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                          )
                          .min(0)
                          .max(1)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                      )
                      .optional(),
                    altText: z
                      .string()
                      .describe('Image alt text, translatable.')
                      .max(5000)
                      .optional()
                      .nullable(),
                  })
                  .describe('image item'),
              }),
            ])
          )
          .describe('media of the item')
          .optional(),
        _createdDate: z.date().optional().nullable(),
        _updatedDate: z.date().optional().nullable(),
        description: z.string().max(1500).optional().nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Data Extensions')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata')
    .optional(),
});
export const UpdateExtendedFieldsRequest = z.object({
  _id: z.string().describe('ID of the entity to update.'),
  namespace: z
    .string()
    .describe(
      'Identifier for the app whose extended fields are being updated.'
    ),
  options: z.object({
    namespaceData: z
      .record(z.string(), z.any())
      .describe(
        'Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured.'
      ),
  }),
});
export const UpdateExtendedFieldsResponse = z.object({
  item: z
    .object({
      _id: z
        .string()
        .describe('Item ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .optional()
        .nullable(),
      draftItemId: z
        .string()
        .describe('Draft Item ID that this item originated from')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      galleryId: z
        .string()
        .describe('Gallery ID that the item is part of')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      siteVersion: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe('Represents the site version when the item was published')
        .optional()
        .nullable(),
      title: z.string().max(50).optional().nullable(),
      link: z
        .object({
          text: z
            .string()
            .describe(
              "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
            )
            .max(1000)
            .optional()
            .nullable(),
          url: z
            .string()
            .describe('Target URL of the link.')
            .url()
            .optional()
            .nullable(),
        })
        .describe('Link to an internal or external page')
        .optional(),
      media: z
        .intersection(
          z.object({
            type: z
              .enum(['UNDEFINED', 'IMAGE'])
              .describe("item's media type")
              .optional(),
          }),
          z.xor([
            z.object({ imageOptions: z.never().optional() }),
            z.object({
              imageOptions: z
                .object({
                  imageInfo: z
                    .string()
                    .describe('Information about the Wix Media image.')
                    .optional(),
                  focalPoint: z
                    .object({
                      x: z
                        .number()
                        .describe('X-coordinate of the focal point.')
                        .optional(),
                      y: z
                        .number()
                        .describe('Y-coordinate of the focal point.')
                        .optional(),
                    })
                    .describe('Focal point of the image.')
                    .optional(),
                  exif: z
                    .record(z.string(), z.any())
                    .describe(
                      'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                    )
                    .optional()
                    .nullable(),
                  quality: z
                    .number()
                    .int()
                    .describe(
                      'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                    )
                    .min(30)
                    .max(100)
                    .optional()
                    .nullable(),
                  unsharpMasking: z
                    .object({
                      amount: z
                        .number()
                        .describe(
                          'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                        )
                        .min(0)
                        .max(5)
                        .optional()
                        .nullable(),
                      radius: z
                        .number()
                        .describe(
                          'Unsharp masking radius in pixels. Controls the sharpening width.'
                        )
                        .optional()
                        .nullable(),
                      threshold: z
                        .number()
                        .describe(
                          'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                        )
                        .min(0)
                        .max(1)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                    )
                    .optional(),
                  altText: z
                    .string()
                    .describe('Image alt text, translatable.')
                    .max(5000)
                    .optional()
                    .nullable(),
                })
                .describe('image item'),
            }),
          ])
        )
        .describe('media of the item')
        .optional(),
      _createdDate: z.date().optional().nullable(),
      _updatedDate: z.date().optional().nullable(),
      description: z.string().max(1500).optional().nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
    })
    .describe('Updated Item.')
    .optional(),
});
export const BulkCreateItemsRequest = z.object({
  items: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .optional()
          .nullable(),
        draftItemId: z
          .string()
          .describe('Draft Item ID that this item originated from')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        galleryId: z
          .string()
          .describe('Gallery ID that the item is part of')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        siteVersion: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe('Represents the site version when the item was published')
          .optional()
          .nullable(),
        title: z.string().max(50).optional().nullable(),
        link: z
          .object({
            text: z
              .string()
              .describe(
                "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
              )
              .max(1000)
              .optional()
              .nullable(),
            url: z
              .string()
              .describe('Target URL of the link.')
              .url()
              .optional()
              .nullable(),
          })
          .describe('Link to an internal or external page')
          .optional(),
        media: z
          .intersection(
            z.object({ type: z.enum(['UNDEFINED', 'IMAGE']).optional() }),
            z.xor([
              z.object({ imageOptions: z.never().optional() }),
              z.object({
                imageOptions: z
                  .object({
                    imageInfo: z
                      .string()
                      .describe('Information about the Wix Media image.')
                      .optional(),
                    focalPoint: z
                      .object({
                        x: z
                          .number()
                          .describe('X-coordinate of the focal point.')
                          .optional(),
                        y: z
                          .number()
                          .describe('Y-coordinate of the focal point.')
                          .optional(),
                      })
                      .describe('Focal point of the image.')
                      .optional(),
                    exif: z
                      .record(z.string(), z.any())
                      .describe(
                        'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                      )
                      .optional()
                      .nullable(),
                    quality: z
                      .number()
                      .int()
                      .describe(
                        'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                      )
                      .min(30)
                      .max(100)
                      .optional()
                      .nullable(),
                    unsharpMasking: z
                      .object({
                        amount: z
                          .number()
                          .describe(
                            'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                          )
                          .min(0)
                          .max(5)
                          .optional()
                          .nullable(),
                        radius: z
                          .number()
                          .describe(
                            'Unsharp masking radius in pixels. Controls the sharpening width.'
                          )
                          .optional()
                          .nullable(),
                        threshold: z
                          .number()
                          .describe(
                            'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                          )
                          .min(0)
                          .max(1)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                      )
                      .optional(),
                    altText: z
                      .string()
                      .describe('Image alt text, translatable.')
                      .max(5000)
                      .optional()
                      .nullable(),
                  })
                  .describe('image item'),
              }),
            ])
          )
          .describe('media of the item')
          .optional(),
        _createdDate: z.date().optional().nullable(),
        _updatedDate: z.date().optional().nullable(),
        description: z.string().max(1500).optional().nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Data Extensions')
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'set to `true` if you wish to receive back the created Items in the response'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata regarding the specific single create operation')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Item ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .optional()
              .nullable(),
            draftItemId: z
              .string()
              .describe('Draft Item ID that this item originated from')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            galleryId: z
              .string()
              .describe('Gallery ID that the item is part of')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            siteVersion: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Represents the site version when the item was published'
              )
              .optional()
              .nullable(),
            title: z.string().max(50).optional().nullable(),
            link: z
              .object({
                text: z
                  .string()
                  .describe(
                    "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                url: z
                  .string()
                  .describe('Target URL of the link.')
                  .url()
                  .optional()
                  .nullable(),
              })
              .describe('Link to an internal or external page')
              .optional(),
            media: z
              .intersection(
                z.object({
                  type: z
                    .enum(['UNDEFINED', 'IMAGE'])
                    .describe("item's media type")
                    .optional(),
                }),
                z.xor([
                  z.object({ imageOptions: z.never().optional() }),
                  z.object({
                    imageOptions: z
                      .object({
                        imageInfo: z
                          .string()
                          .describe('Information about the Wix Media image.')
                          .optional(),
                        focalPoint: z
                          .object({
                            x: z
                              .number()
                              .describe('X-coordinate of the focal point.')
                              .optional(),
                            y: z
                              .number()
                              .describe('Y-coordinate of the focal point.')
                              .optional(),
                          })
                          .describe('Focal point of the image.')
                          .optional(),
                        exif: z
                          .record(z.string(), z.any())
                          .describe(
                            'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                          )
                          .optional()
                          .nullable(),
                        quality: z
                          .number()
                          .int()
                          .describe(
                            'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                          )
                          .min(30)
                          .max(100)
                          .optional()
                          .nullable(),
                        unsharpMasking: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                              )
                              .min(0)
                              .max(5)
                              .optional()
                              .nullable(),
                            radius: z
                              .number()
                              .describe(
                                'Unsharp masking radius in pixels. Controls the sharpening width.'
                              )
                              .optional()
                              .nullable(),
                            threshold: z
                              .number()
                              .describe(
                                'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                              )
                              .min(0)
                              .max(1)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                          )
                          .optional(),
                        altText: z
                          .string()
                          .describe('Image alt text, translatable.')
                          .max(5000)
                          .optional()
                          .nullable(),
                      })
                      .describe('image item'),
                  }),
                ])
              )
              .describe('media of the item')
              .optional(),
            _createdDate: z.date().optional().nullable(),
            _updatedDate: z.date().optional().nullable(),
            description: z.string().max(1500).optional().nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Data Extensions')
              .optional(),
          })
          .describe(
            'Only exists if `returnEntity` was set to true in the request'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata regarding the bulk create operation')
    .optional(),
});
export const BulkUpdateItemsRequest = z.object({
  items: z
    .array(
      z.object({
        item: z
          .object({
            _id: z
              .string()
              .describe('Item ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string'),
            draftItemId: z
              .string()
              .describe('Draft Item ID that this item originated from')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            galleryId: z
              .string()
              .describe('Gallery ID that the item is part of')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            siteVersion: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Represents the site version when the item was published'
              )
              .optional()
              .nullable(),
            title: z.string().max(50).optional().nullable(),
            link: z
              .object({
                text: z
                  .string()
                  .describe(
                    "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                url: z
                  .string()
                  .describe('Target URL of the link.')
                  .url()
                  .optional()
                  .nullable(),
              })
              .describe('Link to an internal or external page')
              .optional(),
            media: z
              .intersection(
                z.object({ type: z.enum(['UNDEFINED', 'IMAGE']).optional() }),
                z.xor([
                  z.object({ imageOptions: z.never().optional() }),
                  z.object({
                    imageOptions: z
                      .object({
                        imageInfo: z
                          .string()
                          .describe('Information about the Wix Media image.')
                          .optional(),
                        focalPoint: z
                          .object({
                            x: z
                              .number()
                              .describe('X-coordinate of the focal point.')
                              .optional(),
                            y: z
                              .number()
                              .describe('Y-coordinate of the focal point.')
                              .optional(),
                          })
                          .describe('Focal point of the image.')
                          .optional(),
                        exif: z
                          .record(z.string(), z.any())
                          .describe(
                            'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                          )
                          .optional()
                          .nullable(),
                        quality: z
                          .number()
                          .int()
                          .describe(
                            'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                          )
                          .min(30)
                          .max(100)
                          .optional()
                          .nullable(),
                        unsharpMasking: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                              )
                              .min(0)
                              .max(5)
                              .optional()
                              .nullable(),
                            radius: z
                              .number()
                              .describe(
                                'Unsharp masking radius in pixels. Controls the sharpening width.'
                              )
                              .optional()
                              .nullable(),
                            threshold: z
                              .number()
                              .describe(
                                'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                              )
                              .min(0)
                              .max(1)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                          )
                          .optional(),
                        altText: z
                          .string()
                          .describe('Image alt text, translatable.')
                          .max(5000)
                          .optional()
                          .nullable(),
                      })
                      .describe('image item'),
                  }),
                ])
              )
              .describe('media of the item')
              .optional(),
            _createdDate: z.date().optional().nullable(),
            _updatedDate: z.date().optional().nullable(),
            description: z.string().max(1500).optional().nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Data Extensions')
              .optional(),
          })
          .describe('Item to be updated, may be partial')
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'set to `true` if you wish to receive back the updated Items in the response'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUpdateItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata regarding the specific single update operation')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Item ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .optional()
              .nullable(),
            draftItemId: z
              .string()
              .describe('Draft Item ID that this item originated from')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            galleryId: z
              .string()
              .describe('Gallery ID that the item is part of')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            siteVersion: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Represents the site version when the item was published'
              )
              .optional()
              .nullable(),
            title: z.string().max(50).optional().nullable(),
            link: z
              .object({
                text: z
                  .string()
                  .describe(
                    "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                url: z
                  .string()
                  .describe('Target URL of the link.')
                  .url()
                  .optional()
                  .nullable(),
              })
              .describe('Link to an internal or external page')
              .optional(),
            media: z
              .intersection(
                z.object({
                  type: z
                    .enum(['UNDEFINED', 'IMAGE'])
                    .describe("item's media type")
                    .optional(),
                }),
                z.xor([
                  z.object({ imageOptions: z.never().optional() }),
                  z.object({
                    imageOptions: z
                      .object({
                        imageInfo: z
                          .string()
                          .describe('Information about the Wix Media image.')
                          .optional(),
                        focalPoint: z
                          .object({
                            x: z
                              .number()
                              .describe('X-coordinate of the focal point.')
                              .optional(),
                            y: z
                              .number()
                              .describe('Y-coordinate of the focal point.')
                              .optional(),
                          })
                          .describe('Focal point of the image.')
                          .optional(),
                        exif: z
                          .record(z.string(), z.any())
                          .describe(
                            'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                          )
                          .optional()
                          .nullable(),
                        quality: z
                          .number()
                          .int()
                          .describe(
                            'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                          )
                          .min(30)
                          .max(100)
                          .optional()
                          .nullable(),
                        unsharpMasking: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                              )
                              .min(0)
                              .max(5)
                              .optional()
                              .nullable(),
                            radius: z
                              .number()
                              .describe(
                                'Unsharp masking radius in pixels. Controls the sharpening width.'
                              )
                              .optional()
                              .nullable(),
                            threshold: z
                              .number()
                              .describe(
                                'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                              )
                              .min(0)
                              .max(1)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                          )
                          .optional(),
                        altText: z
                          .string()
                          .describe('Image alt text, translatable.')
                          .max(5000)
                          .optional()
                          .nullable(),
                      })
                      .describe('image item'),
                  }),
                ])
              )
              .describe('media of the item')
              .optional(),
            _createdDate: z.date().optional().nullable(),
            _updatedDate: z.date().optional().nullable(),
            description: z.string().max(1500).optional().nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Data Extensions')
              .optional(),
          })
          .describe(
            'Only exists if `returnEntity` was set to true in the request'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata regarding the bulk update operation')
    .optional(),
});
export const BulkDeleteItemsRequest = z.object({
  itemIds: z.array(z.string()).min(1).max(100),
});
export const BulkDeleteItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata regarding the specific single delete operation')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata regarding the bulk delete operation')
    .optional(),
});
export const RemoveItemFromTrashBinRequest = z.object({
  itemId: z
    .string()
    .describe('ID of the Item to remove from TrashBin.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const RemoveItemFromTrashBinResponse = z.object({});
export const RestoreItemFromTrashBinRequest = z.object({
  itemId: z
    .string()
    .describe('ID of the Item to restore from TrashBin.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const RestoreItemFromTrashBinResponse = z.object({
  item: z
    .object({
      _id: z
        .string()
        .describe('Item ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .optional()
        .nullable(),
      draftItemId: z
        .string()
        .describe('Draft Item ID that this item originated from')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      galleryId: z
        .string()
        .describe('Gallery ID that the item is part of')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      siteVersion: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe('Represents the site version when the item was published')
        .optional()
        .nullable(),
      title: z.string().max(50).optional().nullable(),
      link: z
        .object({
          text: z
            .string()
            .describe(
              "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
            )
            .max(1000)
            .optional()
            .nullable(),
          url: z
            .string()
            .describe('Target URL of the link.')
            .url()
            .optional()
            .nullable(),
        })
        .describe('Link to an internal or external page')
        .optional(),
      media: z
        .intersection(
          z.object({
            type: z
              .enum(['UNDEFINED', 'IMAGE'])
              .describe("item's media type")
              .optional(),
          }),
          z.xor([
            z.object({ imageOptions: z.never().optional() }),
            z.object({
              imageOptions: z
                .object({
                  imageInfo: z
                    .string()
                    .describe('Information about the Wix Media image.')
                    .optional(),
                  focalPoint: z
                    .object({
                      x: z
                        .number()
                        .describe('X-coordinate of the focal point.')
                        .optional(),
                      y: z
                        .number()
                        .describe('Y-coordinate of the focal point.')
                        .optional(),
                    })
                    .describe('Focal point of the image.')
                    .optional(),
                  exif: z
                    .record(z.string(), z.any())
                    .describe(
                      'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                    )
                    .optional()
                    .nullable(),
                  quality: z
                    .number()
                    .int()
                    .describe(
                      'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                    )
                    .min(30)
                    .max(100)
                    .optional()
                    .nullable(),
                  unsharpMasking: z
                    .object({
                      amount: z
                        .number()
                        .describe(
                          'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                        )
                        .min(0)
                        .max(5)
                        .optional()
                        .nullable(),
                      radius: z
                        .number()
                        .describe(
                          'Unsharp masking radius in pixels. Controls the sharpening width.'
                        )
                        .optional()
                        .nullable(),
                      threshold: z
                        .number()
                        .describe(
                          'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                        )
                        .min(0)
                        .max(1)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                    )
                    .optional(),
                  altText: z
                    .string()
                    .describe('Image alt text, translatable.')
                    .max(5000)
                    .optional()
                    .nullable(),
                })
                .describe('image item'),
            }),
          ])
        )
        .describe('media of the item')
        .optional(),
      _createdDate: z.date().optional().nullable(),
      _updatedDate: z.date().optional().nullable(),
      description: z.string().max(1500).optional().nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
    })
    .describe('Restored Item.')
    .optional(),
});
export const GetDeletedItemRequest = z.object({
  itemId: z
    .string()
    .describe('ID of the Item to retrieve from TrashBin.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetDeletedItemResponse = z.object({
  item: z
    .object({
      _id: z
        .string()
        .describe('Item ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .optional()
        .nullable(),
      draftItemId: z
        .string()
        .describe('Draft Item ID that this item originated from')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      galleryId: z
        .string()
        .describe('Gallery ID that the item is part of')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      siteVersion: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe('Represents the site version when the item was published')
        .optional()
        .nullable(),
      title: z.string().max(50).optional().nullable(),
      link: z
        .object({
          text: z
            .string()
            .describe(
              "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
            )
            .max(1000)
            .optional()
            .nullable(),
          url: z
            .string()
            .describe('Target URL of the link.')
            .url()
            .optional()
            .nullable(),
        })
        .describe('Link to an internal or external page')
        .optional(),
      media: z
        .intersection(
          z.object({
            type: z
              .enum(['UNDEFINED', 'IMAGE'])
              .describe("item's media type")
              .optional(),
          }),
          z.xor([
            z.object({ imageOptions: z.never().optional() }),
            z.object({
              imageOptions: z
                .object({
                  imageInfo: z
                    .string()
                    .describe('Information about the Wix Media image.')
                    .optional(),
                  focalPoint: z
                    .object({
                      x: z
                        .number()
                        .describe('X-coordinate of the focal point.')
                        .optional(),
                      y: z
                        .number()
                        .describe('Y-coordinate of the focal point.')
                        .optional(),
                    })
                    .describe('Focal point of the image.')
                    .optional(),
                  exif: z
                    .record(z.string(), z.any())
                    .describe(
                      'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                    )
                    .optional()
                    .nullable(),
                  quality: z
                    .number()
                    .int()
                    .describe(
                      'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                    )
                    .min(30)
                    .max(100)
                    .optional()
                    .nullable(),
                  unsharpMasking: z
                    .object({
                      amount: z
                        .number()
                        .describe(
                          'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                        )
                        .min(0)
                        .max(5)
                        .optional()
                        .nullable(),
                      radius: z
                        .number()
                        .describe(
                          'Unsharp masking radius in pixels. Controls the sharpening width.'
                        )
                        .optional()
                        .nullable(),
                      threshold: z
                        .number()
                        .describe(
                          'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                        )
                        .min(0)
                        .max(1)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                    )
                    .optional(),
                  altText: z
                    .string()
                    .describe('Image alt text, translatable.')
                    .max(5000)
                    .optional()
                    .nullable(),
                })
                .describe('image item'),
            }),
          ])
        )
        .describe('media of the item')
        .optional(),
      _createdDate: z.date().optional().nullable(),
      _updatedDate: z.date().optional().nullable(),
      description: z.string().max(1500).optional().nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
    })
    .describe('Deleted Item.')
    .optional(),
});
export const ListDeletedItemsRequest = z.object({
  options: z
    .object({
      query: z
        .intersection(
          z.object({
            filter: z
              .record(z.string(), z.any())
              .describe(
                'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
              )
              .optional()
              .nullable(),
            sort: z
              .array(
                z.object({
                  fieldName: z
                    .string()
                    .describe('Name of the field to sort by.')
                    .max(512)
                    .optional(),
                  order: z.enum(['ASC', 'DESC']).optional(),
                })
              )
              .max(5)
              .optional(),
          }),
          z.xor([
            z.object({ cursorPaging: z.never().optional() }),
            z.object({
              cursorPaging: z
                .object({
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of items to return in the results.'
                    )
                    .min(0)
                    .max(100)
                    .optional()
                    .nullable(),
                  cursor: z
                    .string()
                    .describe(
                      "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                    )
                    .max(16000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
                ),
            }),
          ])
        )
        .describe('WQL query to run against TrashBin items')
        .optional(),
      itemIds: z.array(z.string()).max(100).optional(),
    })
    .optional(),
});
export const ListDeletedItemsResponse = z.object({
  items: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .optional()
          .nullable(),
        draftItemId: z
          .string()
          .describe('Draft Item ID that this item originated from')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        galleryId: z
          .string()
          .describe('Gallery ID that the item is part of')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        siteVersion: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe('Represents the site version when the item was published')
          .optional()
          .nullable(),
        title: z.string().max(50).optional().nullable(),
        link: z
          .object({
            text: z
              .string()
              .describe(
                "Display text of the link.\n`referenced_entity_field` carries this field's translations from a source entity onto an entity\nthat references it - concretely, DraftItem -> the published Item, whose translations are synced\nfrom DraftItem rather than owned locally. Without it the translated label is served on the draft\nread (so the editor looks right) and silently falls back to the origin language on the published\nread, which is what site visitors get. It must be declared on this leaf: the collector only\nhonours the annotation on STRING / StringValue / RichContent and ignores it on message fields.\nThe path is the field's location in the source entity; every embedder of this type - pro-gallery\nand fast-gallery, draft and published - places it at `link`, so one path is correct for all."
              )
              .max(1000)
              .optional()
              .nullable(),
            url: z
              .string()
              .describe('Target URL of the link.')
              .url()
              .optional()
              .nullable(),
          })
          .describe('Link to an internal or external page')
          .optional(),
        media: z
          .intersection(
            z.object({
              type: z
                .enum(['UNDEFINED', 'IMAGE'])
                .describe("item's media type")
                .optional(),
            }),
            z.xor([
              z.object({ imageOptions: z.never().optional() }),
              z.object({
                imageOptions: z
                  .object({
                    imageInfo: z
                      .string()
                      .describe('Information about the Wix Media image.')
                      .optional(),
                    focalPoint: z
                      .object({
                        x: z
                          .number()
                          .describe('X-coordinate of the focal point.')
                          .optional(),
                        y: z
                          .number()
                          .describe('Y-coordinate of the focal point.')
                          .optional(),
                      })
                      .describe('Focal point of the image.')
                      .optional(),
                    exif: z
                      .record(z.string(), z.any())
                      .describe(
                        'Set of key-value pairs describing the media in [Exchangeable Image File format](https://en.wikipedia.org/wiki/Exif).'
                      )
                      .optional()
                      .nullable(),
                    quality: z
                      .number()
                      .int()
                      .describe(
                        'Image compression level. <br />\n\nMin: `30` <br />\nMax: `100`'
                      )
                      .min(30)
                      .max(100)
                      .optional()
                      .nullable(),
                    unsharpMasking: z
                      .object({
                        amount: z
                          .number()
                          .describe(
                            'Unsharp masking amount. Controls the sharpening strength. <br />\n\nMin: `0` <br />\nMax: `5`'
                          )
                          .min(0)
                          .max(5)
                          .optional()
                          .nullable(),
                        radius: z
                          .number()
                          .describe(
                            'Unsharp masking radius in pixels. Controls the sharpening width.'
                          )
                          .optional()
                          .nullable(),
                        threshold: z
                          .number()
                          .describe(
                            'Unsharp masking threshold. Controls how different neighboring pixels must be for shapening to apply. <br />\n\nMin: `0` <br />\nMax: `1`'
                          )
                          .min(0)
                          .max(1)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        '[Unsharp masking](https://en.wikipedia.org/wiki/Unsharp_masking) values of the image.'
                      )
                      .optional(),
                    altText: z
                      .string()
                      .describe('Image alt text, translatable.')
                      .max(5000)
                      .optional()
                      .nullable(),
                  })
                  .describe('image item'),
              }),
            ])
          )
          .describe('media of the item')
          .optional(),
        _createdDate: z.date().optional().nullable(),
        _updatedDate: z.date().optional().nullable(),
        description: z.string().max(1500).optional().nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Data Extensions')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata')
    .optional(),
});
